import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Avatar,
  Button,
  TextField,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import AddContactModal from "./AddContactModal";
import API_URLS, { BASE_URL } from "../../../constants/apiConstants";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { DeleteConfirmationDialog } from "./DeleteConfirmationDialog";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../context/AuthProvider";

const ContactsTable = ({ contacts, studentId }) => {
  const [localContacts, setLocalContacts] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [editedContact, setEditedContact] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { school, auth } = useContext(AuthContext);


  const columnLabels = [
    t("studentDetails.contactsCard.name"),
    t("studentDetails.contactsCard.email"),
    t("studentDetails.contactsCard.phone"),
    t("studentDetails.contactsCard.canPickUp"),
    t("studentDetails.contactsCard.signedUp"),
    t("studentDetails.contactsCard.contactType"),
    t("studentDetails.contactsCard.contactStatus"),
    t("actions.actions"),
  ];

  const [newContact, setNewContact] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    can_pick_up: false,
    signed_up: false,
    contact_type: "",
    contact_status: "",
    image: ""
  });

  useEffect(() => {
    setLocalContacts(contacts);
  }, [contacts]);

  const capitalizeFirst = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

  const handleEditClick = (index) => {
    setEditingIndex(index);
    setEditedContact({ ...localContacts[index] });
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditedContact({});
  };

  const handleChange = (field, value) => {
    setEditedContact((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewContactChange = (field, value) => {
    setNewContact((prev) => ({ ...prev, [field]: value }));
    // Clear the field-level error as soon as the user starts typing a valid value
    if (fieldErrors[field] && value.trim() !== "") {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateNewContact = (contact) => {
    const errors = {};
    if (!contact.first_name || !contact.first_name.trim()) {
      errors.first_name = t("forms.required_field");
    }
    if (!contact.last_name || !contact.last_name.trim()) {
      errors.last_name = t("forms.required_field");
    }
    return errors;
  };

  const handleDeleteOpen = (index) => {
    setDeleteIndex(index);
    setIsDelete(true);
  }

  const handleDeleteClose = () => {
    setIsDelete(false);
  }

  const getChanges = () => {
    const original = localContacts[editingIndex];
    const changes = {};
    for (const key in editedContact) {
      if (editedContact[key] !== original[key]) {
        if (key === "name") {
        const parts = editedContact[key].trim().split(" ");
        changes.first_name = parts[0] || "";
        changes.last_name = parts.slice(1).join(" ") || "";
      } else {
        changes[key] = editedContact[key]; 
      }
      }
    }
    return changes;
  };

  const updateData = async (data) => {
    try {
      setLoading(true);
      await axiosPrivate.patch(
        API_URLS.updateStudentContacts(
          school.id,
          studentId,
          localContacts[editingIndex].id
        ),
        data
      );
    } catch (err) {
      setError("Failed to update contact.");
    } finally {
      setLoading(false);
    }
  };

    const handleDelete = async () => {
      try {
        setLoading(true);
        await axiosPrivate.delete(API_URLS.updateStudentContacts(school.id, studentId, localContacts[deleteIndex].id));
        navigate(0);
      } catch (error) {
        console.error("Delete request error:", error);
      } finally {
        setLoading(false);
      }
    };

  const handleSave = async () => {
    const updated = [...localContacts];
    updated[editingIndex] = {
      ...localContacts[editingIndex],
      ...editedContact,
    };
    const diff = getChanges();
    await updateData(diff);
    setLocalContacts(updated);
    setEditingIndex(null);
    setEditedContact({});
  };

    const addContact = async (data) => {
    try {
      setLoading(true);
      await axiosPrivate.post(
        API_URLS.getStudentContacts(
          studentId,
          school.id
        ),
        data
      );
      navigate(0);
    } catch (err) {
      setError("Failed to add contact.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddContactSave = async () => {
    const errors = validateNewContact(newContact);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    try {
      setLoading(true);
      await addContact(newContact);
      handleCloseModal();
    } catch (err) {
      setError("Could not add contact.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setFieldErrors({});
    setError(null);
    setNewContact({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      can_pick_up: false,
      signed_up: false,
      contact_type: "",
      contact_status: "",
      image: "",
    });
  };

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        backgroundColor: "white",
        overflowX: "auto",
      }}
    >
      <Box
        className="header"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#f2f2f2",
          borderBottom: "1px solid #f0f0f0",
          borderRadius: "8px 8px 0 0",
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="header" fontWeight="bold" color="#333">
          {t("studentDetails.contactsCard.title")}
        </Typography>
      </Box>

      <Box
        sx={{
          px: 2,
        }}
      >
        <Table sx={{ minWidth: 65 }}>
          <TableHead>
            <TableRow>
              {columnLabels.map((label) => (
                <TableCell
                  key={label}
                  align={i18n.dir() === "rtl"?"right":"left"}
                  sx={{
                    fontWeight: 600,
                    fontSize: "medium",
                    borderBottom: "1px solid #ccc",
                    px:1,
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {localContacts.map((contact, i) => (
              <TableRow
                key={i}
                hover
                sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
              >
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {contact.image ? (
                      <Avatar src={BASE_URL + contact.image} />
                    ) : (
                      <Avatar
                        sx={{
                          bgcolor: "#d0d0d0",
                          width: 34,
                          height: 34,
                          color: "black",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                        }}
                      >
                        {contact.initials}
                      </Avatar>
                    )}
                    {editingIndex === i ? (
                      <TextField
                        size="small"
                        value={editedContact.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                      />
                    ) : (
                      <Typography>{contact.name}</Typography>
                    )}
                  </Box>
                </TableCell>

                {["email", "phone"].map(
                  (field) => (
                    <TableCell key={field}>
                      {editingIndex === i ? (
                        <TextField
                          size="small"
                          value={editedContact[field] || ""}
                          onChange={(e) => handleChange(field, e.target.value)}
                        />
                      ) : (
                        contact[field] || "—"
                      )}
                    </TableCell>
                  )
                )}

                {["canPickUp", "signedUp"].map((field) => (
                  <TableCell key={field}>
                    {editingIndex === i ? (
                      <Select
                        size="small"
                        value={editedContact[field]}
                        onChange={(e) =>
                          handleChange(field, e.target.value === "true")
                        }
                      >
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    ) : contact[field] ? (
                      "Yes"
                    ) : (
                      "No"
                    )}
                  </TableCell>
                ))}

                 {["contactType", "contactStatus"].map(
                  (field) => (
                    <TableCell key={field}>
                      {editingIndex === i ? (
                        <TextField
                          size="small"
                          value={editedContact[field] || ""}
                          onChange={(e) => handleChange(field, e.target.value)}
                        />
                      ) : (
                        contact[field] || "—"
                      )}
                    </TableCell>
                  )
                )}

                <TableCell sx={{px:0}}>
                  {editingIndex === i ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        sx={{ color: "#702E8F", textTransform: "none" }}
                        onClick={handleSave}
                        disabled={loading}
                      >
                        {t("actions.save")}
                      </Button>
                      <Button
                        size="small"
                        sx={{ color: "#dc3545", textTransform: "none" }}
                        onClick={handleCancel}
                      >
                        {t("actions.cancel")}
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{display:"flex"}}>
                      <Button
                      size="small"
                      sx={{ color: "#702E8F", textTransform: "none", px:0 }}
                      onClick={() => handleEditClick(i)}
                    >
                      {t("actions.edit")}
                    </Button>
                    <Button
                      size="small"
                      sx={{ color: "#dc3545", textTransform: "none", px:0 }}
                      onClick={()=>handleDeleteOpen(i)}
                    >
                      {t("actions.delete")}
                    </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Button
        onClick={() => setShowAddModal(true)}
        sx={{
          mt: 2,
          ml: 2,
          color: "#702E8F",
          fontWeight: 600,
          fontSize: "medium",
          textTransform: "none",
        }}
      >
        + {t("studentDetails.contactsCard.addContact")}
      </Button>

      <AddContactModal
        newContact={newContact}
        handleAddContactSave={handleAddContactSave}
        error={error}
        fieldErrors={fieldErrors}
        handleNewContactChange={handleNewContactChange}
        loading={loading}
        open={showAddModal}
        handleClose={handleCloseModal}
      />
      {isDelete&&(<DeleteConfirmationDialog onCancel={handleDeleteClose} onDelete={handleDelete}/>)}
    </Box>
  );
};

export default ContactsTable;